/**
 * LanguageSelector Component
 * Dropdown for selecting the app language on landing page
 */
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, X } from 'lucide-react';
import { LANGUAGES, getLanguageByCode } from '../constants/languages';

const LanguageSelector = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Extract base language code (e.g., "de-DE" -> "de")
  const baseLanguage = i18n.language?.split('-')[0] || 'en';
  const currentLanguage = getLanguageByCode(baseLanguage) || LANGUAGES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('userLanguage', langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all ${
          compact 
            ? 'bg-white/10 hover:bg-white/20 text-white' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
        aria-label="Select language"
      >
        <Globe className="w-4 h-4" />
        <span className="text-lg">{currentLanguage.flag}</span>
        {!compact && <span className="text-sm font-medium hidden sm:inline">{currentLanguage.name}</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Mobile: Full-screen modal, Desktop: Dropdown */}
      {isOpen && (
        <>
          {/* Mobile overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 sm:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Language list */}
          <div className="fixed sm:absolute left-2 right-2 sm:left-0 sm:right-auto bottom-2 sm:bottom-auto sm:mt-2 w-auto sm:w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 max-h-[70vh] sm:max-h-80 overflow-y-auto">
            {/* Mobile close header */}
            <div className="flex items-center justify-between px-4 py-2 border-b sm:hidden">
              <span className="font-medium text-gray-700">Sprache / Language</span>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full px-4 py-3 sm:py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                  currentLanguage.code === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
                }`}
              >
                <span className="text-xl">{lang.flag}</span>
                <span className="text-base sm:text-sm font-medium">{lang.name}</span>
                {currentLanguage.code === lang.code && (
                  <span className="ml-auto text-emerald-500">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
